import {Component} from '@angular/core'
import { Router, ActivatedRoute} from '@angular/router'
import { NuclearObservable } from '../nuclear-observable'
import { BaseComponent } from '../components/base.component'
import {customerGetters} from '../stores/customer-store'
import {Customer, Plan, Account} from '../types'
import { AccountActions } from '../actions/account-actions'
import { PlanActions } from '../actions/plan-actions'
import { SubscriptionActions } from '../actions/subscription-actions'
import { fullDayWithYear, planChangeSummaryPath } from '../utils'
import { xsrfToken } from '../request'
import moment from 'moment'
import { CustomerActions } from 'src/actions/customer-actions'
import { VideoPlanFormOutput } from 'src/modules/plan-form/video-plan-form.component'
import { Apollo } from 'apollo-angular'

@Component({
  selector: 'plan-page',
  template: `
    <plan-form *ngIf="showForm() && !video()"
               [plans]="plans" [customer]="customer" [account]="account"
               (onCreate)="navigateToTopicsPage()">
    </plan-form>
    <ng-template [ngIf]="video()">
      <h3 class="h3-subheader text-center mt-5">How many videos do you want per month?</h3>
      <video-plan-form *ngIf="showForm()"
                      [customerId]="customer?.id" [plans]="plans" [quantity]="customer?.subscription?.quantity"
                      [buttonText]="'Change my plan'" [learnMore]="false"
                      (submit)="previewPlanChange($event)">
      </video-plan-form>
    </ng-template>
    <div class="notice big-top-buffer" *ngIf="pastDue()">
      You can't switch your plan because your account is past due.
      Please update your <a [routerLink]="['/dashboard', 'billing_info', 'edit']">payment info</a> or contact support for assistance.
    </div>
    <div class="big-top-buffer text-center" *ngIf="customer?.subscription?.state === 'canceled'">
      <h3>Subscription Status: Canceled</h3>
      <p>You will have access to your submissions until {{fullDayWithYear(dashboardAccessUntil())}}.</p>
      <p>If you wish to receive submissions past that date just click the button below and your subscription will continue as normal.</p>

      <form ngNoForm class="top-buffer" method="post" action="/customers/{{customer?.id}}/subscription/reactivate">
        <input type="hidden" name="authenticity_token" value="{{xsrfToken}}">
        <input class="btn orange-button" action="reactivate" type="submit" value="Reactivate Subscription">
      </form>
    </div>
  `,
})
export class PlanPage extends BaseComponent {
  customer: Customer;
  account: Account;
  plans: Plan[] = [];

  constructor(private route: ActivatedRoute,
              private router: Router,
              private planActions: PlanActions,
              private subscriptionActions: SubscriptionActions,
              private accountActions: AccountActions,
              private customerActions: CustomerActions,
              private apollo: Apollo) {
    super()
  }

  ngOnInit(): void {
    this.accountActions.loadAccount()
    this.observeAccount(a => this.account = a)

    // Load Plans
    this.planActions.loadPlans()
    const planObs = NuclearObservable.from<Plan[]>(customerGetters.plans, { toJS: true }).subscribe(ps => this.plans = ps)

    const routeObs = this.route.parent.params.subscribe(params => {
      const customerId = +params['customerId']
      this.customerActions.loadCustomer(customerId)
      this.observeCustomer(customerId, c => {
        if (c?.multipass || c?.canBeMigratedFromExpired) {
          this.router.navigate(['dashboard',customerId, 'multipass-plan'])
        } else {
          this.customer = c
        }
        // Load Subscription - to look at previous subscription for plan-form prepopulation
        this.subscriptionActions.loadSubscription(customerId)
      })

    })

    this.observers.push(routeObs, planObs)
  }

  showForm(): boolean {
    return this.customer &&
      this.plans.length > 0 &&
      (!this.customer.subscription || (this.customer.subscription.state !== 'canceled' && !this.pastDue()))
  }

  video(): boolean {
    return this.customer && this.customer.subscription && this.customer.subscription.plan.code.includes('video')
  }

  pastDue(): boolean {
    if (this.customer && this.customer.subscription) {
      return this.customer.state !== 'expired' && !!this.customer.subscription.pastDueAt
    } else {
      return false
    }
  }

  navigateToTopicsPage(): void {
    this.customerActions.loadCustomerWithSubscription(this.customer.id).then(() => {
      // Refetch all active queries (to update subscription header)
      this.apollo.getClient().resetStore()
      this.router.navigate(['/dashboard', this.customer.id, 'topics'])
    })
  }

  dashboardAccessUntil(): Date {
    return moment(this.customer.expiresAt).toDate()
  }

  previewPlanChange(videoPlanOutput: VideoPlanFormOutput): void {
    const path = planChangeSummaryPath(videoPlanOutput)
    window.location.assign(path)
  }

  fullDayWithYear = fullDayWithYear;
  xsrfToken = xsrfToken;
}
